/**
 * Authentication context and hook.
 *
 * Provides login/logout functions, user info, and role checks to the
 * entire component tree via React Context.  The JWT token is persisted
 * in localStorage and validated against GET /api/auth/me on first load
 * to prevent stale-token flicker.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { loginApi, getMeApi } from '../utils/api';

// ---------------------------------------------------------------------------
// localStorage keys (shared with api.js interceptors)
// ---------------------------------------------------------------------------
const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------
const AuthContext = createContext(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

/**
 * Wraps the application and provides authentication state.
 *
 * Must be rendered inside a <BrowserRouter> because it uses
 * useNavigate for the logout redirect.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // On mount: restore session from localStorage, then validate via /me
  useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY);

    if (!storedToken) {
      setLoading(false);
      return;
    }

    // Optimistically set token so interceptor can use it immediately
    setToken(storedToken);

    // Optimistically render cached user (avoids blank while /me loads)
    const cachedUser = localStorage.getItem(USER_KEY);
    if (cachedUser) {
      try {
        setUser(JSON.parse(cachedUser));
      } catch {
        // Corrupted cache — ignore
      }
    }

    // Validate the token against the backend
    getMeApi()
      .then((data) => {
        const validatedUser = { username: data.username, role: data.role };
        setUser(validatedUser);
        localStorage.setItem(USER_KEY, JSON.stringify(validatedUser));
      })
      .catch(() => {
        // Token expired or invalid — clear everything
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setToken(null);
        setUser(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // -----------------------------------------------------------------------
  // Login
  // -----------------------------------------------------------------------
  const login = useCallback(async (username, password, rememberMe = false) => {
    const data = await loginApi(username, password, rememberMe);
    const newToken = data.access_token;
    const newUser = { username: data.user.username, role: data.user.role };

    localStorage.setItem(TOKEN_KEY, newToken);
    localStorage.setItem(USER_KEY, JSON.stringify(newUser));

    setToken(newToken);
    setUser(newUser);

    return data;
  }, []);

  // -----------------------------------------------------------------------
  // Logout
  // -----------------------------------------------------------------------
  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
    navigate('/login');
  }, [navigate]);

  // -----------------------------------------------------------------------
  // Context value
  // -----------------------------------------------------------------------
  const value = {
    user,
    token,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    loading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Access authentication state and actions.
 *
 * Must be called from within an <AuthProvider>.
 *
 * @returns {{
 *   user: {username: string, role: string} | null,
 *   token: string | null,
 *   isAuthenticated: boolean,
 *   isAdmin: boolean,
 *   loading: boolean,
 *   login: (username: string, password: string, rememberMe?: boolean) => Promise,
 *   logout: () => void,
 * }}
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
